import React, { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Store as StoreIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  PhotoLibrary as PhotoLibraryIcon,
  DirectionsCar as DirectionsCarIcon,
  Power as PowerIcon,
  Wc as WcIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { getEventById, getEventStats } from '../services/eventService';
import { apiWithAuth } from '../services/api';
import { submitApplication } from '../services/applicationService';
import { getImageUrl } from '../utils/imageUtils';
import Spinner from '../components/common/Spinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';

const EventDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { eventId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicationNote, setApplicationNote] = useState('');
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [applicationFile, setApplicationFile] = useState(null);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editImageFile, setEditImageFile] = useState(null);

  const { data: event, isLoading, error: fetchError } = useQuery(
    ['event', eventId],
    () => getEventById(eventId)
  );

  const { data: eventStats } = useQuery(
    ['eventStats', eventId],
    () => getEventStats(eventId),
    {
      enabled: !!eventId,
      refetchInterval: 10000, // Refetch every 10 seconds to keep stats updated (especially for payment confirmations)
      refetchOnWindowFocus: true, // Refetch when user comes back to the tab
      staleTime: 5000, // Consider data stale after 5 seconds
    }
  );

  // Fetch current vendor applications to determine this event's application status
  const { data: myApplications = [] } = useQuery(
    ['myApplications'],
    async () => {
      const token = localStorage.getItem('token');
      const resp = await apiWithAuth(token).get('/applications/');
      return resp.data || [];
    },
    { staleTime: 60_000 }
  );

  const myAppForThisEvent = Array.isArray(myApplications)
    ? myApplications.find((a) => a?.event?.event_id === Number(eventId))
    : undefined;

  // Check if application deadline has passed
  const isApplicationDeadlinePassed = useMemo(() => {
    if (!event?.application_deadline) return false;
    const deadline = new Date(event.application_deadline);
    const now = new Date();
    return now > deadline;
  }, [event?.application_deadline]);

  const submitApplicationMutation = useMutation(
    ({ eventId, note, tierId, file, checkboxes, questionAnswers }) => submitApplication(eventId, { additionalNotes: note }, tierId, file, checkboxes, questionAnswers),
    {
    onSuccess: () => {
      queryClient.invalidateQueries(['event', eventId]);
      queryClient.invalidateQueries(['eventStats', eventId]);
      queryClient.invalidateQueries(['myApplications']);
      // Force immediate refetch of stats
      queryClient.refetchQueries(['eventStats', eventId]);
      setApplicationDialogOpen(false);
      setApplicationNote('');
      setSelectedTierId(null);
      setSelectedCheckboxes([]);
      setQuestionAnswers({});
      setApplicationFile(null);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to submit application';
      setError(errorMessage);
      // If error is about capacity, show a more user-friendly message
      if (errorMessage.includes('capacity') || errorMessage.includes('full')) {
        setError('This event is currently full. Your application has been added to the waitlist.');
      }
    },
    }
  );

  const handleApply = () => {
    submitApplicationMutation.mutate({
      eventId: eventId,
      note: applicationNote,
      tierId: selectedTierId,
      file: applicationFile,
      checkboxes: selectedCheckboxes,
      questionAnswers: questionAnswers,
    });
  };

  const handleCheckboxChange = (checkboxId) => {
    setSelectedCheckboxes(prev => {
      if (prev.includes(checkboxId)) {
        return prev.filter(id => id !== checkboxId);
      } else {
        return [...prev, checkboxId];
      }
    });
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Check if current user is an organizer
  const isOrganizer = Number(currentUser?.role_id) === 2;

  // Fetch organizer profile to check ownership
  const { data: organizerProfile, isLoading: profileLoading } = useQuery(
    ['organizerProfile'],
    async () => {
      const token = localStorage.getItem('token');
      const resp = await apiWithAuth(token).get('/organizer/profile');
      console.log('Organizer profile fetched:', resp.data);
      return resp.data;
    },
    { enabled: isOrganizer, retry: false }
  );

  // Check if current user owns this event
  // Need to wait for both event and organizerProfile to load before checking ownership
  const isEventOwner = useMemo(() => {
    console.log('Ownership check inputs:', {
      isOrganizer,
      hasProfile: !!organizerProfile,
      hasEvent: !!event,
      eventOrganizerId: event?.organizer_id,
      profileOrganizerId: organizerProfile?.organizer_id,
      profileData: organizerProfile
    });
    
    if (!isOrganizer || !event) {
      return false;
    }
    
    // If organizerProfile is still loading, don't show button yet
    if (profileLoading) {
      return false;
    }
    
    // If no organizerProfile but user is organizer, they might not have profile yet
    if (!organizerProfile) {
      return false;
    }
    
    const eventOrgId = event.organizer_id;
    const profileOrgId = organizerProfile.organizer_id;
    
    console.log('Comparing IDs:', {
      eventOrganizerId: eventOrgId,
      profileOrganizerId: profileOrgId,
      types: { event: typeof eventOrgId, profile: typeof profileOrgId },
      match: eventOrgId === profileOrgId,
      strictMatch: Number(eventOrgId) === Number(profileOrgId)
    });
    
    // Compare as numbers to handle type differences
    return eventOrgId && profileOrgId && Number(eventOrgId) === Number(profileOrgId);
  }, [isOrganizer, organizerProfile, event, profileLoading]);

  const handleEditClick = () => {
    // Initialize edit form data with current event data
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? new Date(event.date).toISOString().slice(0, 16) : '',
      is_permanent: event.is_permanent || false,
      application_deadline: event.application_deadline ? new Date(event.application_deadline).toISOString().slice(0, 16) : '',
      location: event.location || '',
      capacity: event.capacity || '',
      fee: event.fee || '',
      payment_instructions: event.payment_instructions || '',
      payment_deadline_days: event.payment_deadline_days || '7',
      setup_time: event.setup_time || '',
      power_availability: event.power_availability || '',
      required_documents: event.required_documents || '',
      vendor_requirements: event.vendor_requirements || '',
      equipment_needed: event.equipment_needed || '',
      additional_requirements: event.additional_requirements || '',
      cancellation_policy: event.cancellation_policy || '',
      refund_policy: event.refund_policy || '',
      additional_notes: event.additional_notes || '',
      facilities: event.facilities || '',
      security_measures: event.security_measures || '',
      insurance_requirements: event.insurance_requirements || '',
      safety_guidelines: event.safety_guidelines || '',
    });
    setEditImageFile(null);
    setEditDialogOpen(true);
  };

  const updateEventMutation = useMutation(
    async ({ formData, imageFile }) => {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Always include all fields from editFormData, even if empty, so we can update/clear them
      Object.keys(formData).forEach(key => {
        if (key === 'is_permanent') {
          formDataToSend.append(key, formData[key] ? 'true' : 'false');
        } else if (key === 'date' || key === 'application_deadline') {
          if (formData[key]) {
            formDataToSend.append(key, new Date(formData[key]).toISOString());
          } else if (key === 'application_deadline') {
            // Allow clearing the application deadline by sending empty string
            formDataToSend.append(key, '');
          } else if (key === 'date' && formData.is_permanent) {
            // For permanent events, allow clearing the date
            formDataToSend.append(key, '');
          }
        } else if (key === 'fee') {
          // Always send fee field - if empty string, it will clear the fee
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            const feeValue = parseFloat(formData[key]);
            if (!isNaN(feeValue) && feeValue >= 0) {
              formDataToSend.append(key, feeValue);
            } else {
              // Invalid value, send empty string to clear
              formDataToSend.append(key, '');
            }
          } else {
            // Empty value, send empty string to clear fee
            formDataToSend.append(key, '');
          }
        } else if (key === 'payment_deadline_days') {
          // Handle payment deadline days
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            formDataToSend.append(key, parseInt(formData[key], 10));
          }
        } else {
          // For all other fields, only include if they have a value
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Add image file if provided
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      // Log FormData contents for debugging
      console.log('Sending update request with FormData:');
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      const response = await apiWithAuth(token).put(`/events/${eventId}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Update response:', response.data);
      return response.data;
    },
    {
      onSuccess: () => {
      queryClient.invalidateQueries(['event', eventId]);
      queryClient.invalidateQueries(['eventStats', eventId]);
      queryClient.invalidateQueries(['eventApplications', eventId]);
      queryClient.invalidateQueries('notifications');
      setEditDialogOpen(false);
        setError(null); // Clear any previous errors
      },
      onError: (error) => {
        setError(error.response?.data?.detail || error.message || 'Failed to update event');
      },
    }
  );

  const handleSaveEdit = () => {
    console.log('Saving edit with formData:', editFormData, 'imageFile:', editImageFile);
    updateEventMutation.mutate({ formData: editFormData, imageFile: editImageFile });
  };

  if (isLoading) return <Spinner />;
  if (fetchError) return <ErrorMessage message={fetchError.message} />;
  if (!event) return <ErrorMessage message="Event not found" />;

  const eventImageUrl = event.image_url ? getImageUrl(event.image_url) : null;
  const hasEventImage = !!eventImageUrl;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section with Image */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        {/* Hero Image Section - More Prominent */}
        <Box
          sx={{
            height: { xs: 250, sm: 350, md: 450 },
            backgroundImage: eventImageUrl 
              ? `url(${eventImageUrl})` 
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: hasEventImage 
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.6))' 
                : 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))',
            }
          }}
        >
          {/* Edit Button */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 8, md: 16 },
              right: { xs: 8, md: 16 },
              display: 'flex',
              gap: 1,
              zIndex: 2,
            }}
          >
            {isOrganizer && !profileLoading && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                disabled={!isEventOwner}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  bgcolor: isEventOwner ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)', 
                  color: 'text.primary',
                  fontSize: { xs: '0.8125rem', md: '0.9375rem' },
                  minHeight: { xs: 40, md: 48 },
                  '&:hover': {
                    bgcolor: isEventOwner ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.7)',
                  }
                }}
              >
                {isEventOwner ? 'Edit Event' : 'View Only'}
              </Button>
            )}
          </Box>

          {/* Title and Info Overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: { xs: 3, md: 4 },
              color: 'white',
              zIndex: 1,
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 2, textShadow: '0 2px 8px rgba(0,0,0,0.5)', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              {event.title}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<EventIcon />}
                label={event.is_permanent ? 'Permanent Event' : (event.date ? format(new Date(event.date), 'MMMM d, yyyy') : 'No date set')}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.95)',
                  color: 'text.primary',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: theme.palette.primary.main }
                }}
              />
              <Chip
                icon={<LocationIcon />}
                label={event.location}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.95)',
                  color: 'text.primary',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: theme.palette.primary.main }
                }}
              />
              {event.capacity && (
                <Chip
                  icon={<PeopleIcon />}
                  label={`${event.capacity} vendor spots`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.95)',
                    color: 'text.primary',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: theme.palette.primary.main }
                  }}
                />
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Tabs Section */}
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              sx={{
                borderBottom: 2,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: { xs: 48, md: 64 },
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  px: { xs: 1.5, md: 3 },
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab label="Overview" />
              <Tab label="Vendor Info" />
              <Tab label="Gallery" />
              <Tab label="Requirements" />
            </Tabs>

            {/* Overview Tab */}
            {selectedTab === 0 && (
              <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Grid container spacing={{ xs: 2, md: 4 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      mb: 3, 
                      p: 3, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <DescriptionIcon color="primary" fontSize="large" />
                        Description
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-line',
                          fontSize: '1.05rem',
                          lineHeight: 1.8,
                          color: 'text.primary',
                          fontWeight: 400,
                        }}
                      >
                        {event.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      mb: 3,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.secondary.main, 0.02),
                      borderLeft: `4px solid ${theme.palette.secondary.main}`,
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <CategoryIcon color="primary" fontSize="large" />
                        Categories
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {event.categories?.map((category) => (
                          <Chip
                            key={category.id}
                            label={category.name}
                            variant="filled"
                            size="medium"
                            sx={{ 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2)
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Vendor Info Tab */}
            {selectedTab === 1 && (
              <Box sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: alpha(theme.palette.info.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <DirectionsCarIcon color="primary" fontSize="large" />
                        Setup Information
                      </Typography>
                      <List sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                        <ListItem>
                          <ListItemIcon>
                            <AccessTimeIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Setup Time
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.setup_time ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.setup_time ? 'italic' : 'normal',
                              }}>
                                {event.setup_time || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <PowerIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Power Availability
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.power_availability ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.power_availability ? 'italic' : 'normal',
                              }}>
                                {event.power_availability || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <WcIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Facilities
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.facilities ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.facilities ? 'italic' : 'normal',
                              }}>
                                {event.facilities || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: alpha(theme.palette.success.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <SecurityIcon color="primary" fontSize="large" />
                        Security & Safety
                      </Typography>
                      <List sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                        <ListItem>
                          <ListItemIcon>
                            <SecurityIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Security Measures
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.security_measures ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.security_measures ? 'italic' : 'normal',
                              }}>
                                {event.security_measures || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <InfoIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Insurance Requirements
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.insurance_requirements ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.insurance_requirements ? 'italic' : 'normal',
                              }}>
                                {event.insurance_requirements || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <WarningIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Safety Guidelines
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.safety_guidelines ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.safety_guidelines ? 'italic' : 'normal',
                              }}>
                                {event.safety_guidelines || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Gallery Tab */}
            {selectedTab === 2 && (
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary' }}>
                    <PhotoLibraryIcon color="primary" fontSize="large" />
                    Event Gallery
                  </Typography>
                  {isOrganizer && isEventOwner && (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={handleEditClick}
                    >
                      {eventImageUrl ? 'Edit Image' : 'Upload Image'}
                    </Button>
                  )}
                </Box>
                {eventImageUrl ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Event Poster</Typography>
                    <Box
                      component="img"
                      src={eventImageUrl}
                      alt={event.title}
                      sx={{
                        width: '100%',
                        maxHeight: 500,
                        objectFit: 'contain',
                        borderRadius: 2,
                        boxShadow: 2,
                        cursor: 'pointer',
                        mb: 3,
                      }}
                      onClick={() => window.open(eventImageUrl, '_blank')}
                    />
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    No event poster/image available yet.
                    {isOrganizer && isEventOwner && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={handleEditClick}
                        >
                          Upload Image
                        </Button>
                      </Box>
                    )}
                  </Alert>
                )}
                {event.images && event.images.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>Additional Images</Typography>
                    <ImageList sx={{ width: '100%', height: 450 }} cols={3} rowHeight={200} gap={8}>
                      {event.images.map((image) => (
                        <ImageListItem key={image.id}>
                          <img
                            src={getImageUrl(image.url)}
                            alt={image.caption || 'Event image'}
                            loading="lazy"
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                          />
                          {image.caption && (
                            <ImageListItemBar
                              title={image.caption}
                              subtitle={image.description}
                            />
                          )}
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </>
                )}
              </Box>
            )}

            {/* Requirements Tab */}
            {selectedTab === 3 && (
              <Box sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <CheckCircleIcon color="primary" fontSize="large" />
                        Vendor Requirements
                      </Typography>
                      <List sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Required Documents
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.required_documents ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.required_documents ? 'italic' : 'normal',
                              }}>
                                {event.required_documents || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Equipment Needed
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.equipment_needed ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.equipment_needed ? 'italic' : 'normal',
                              }}>
                                {event.equipment_needed || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Additional Requirements
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.additional_requirements ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.additional_requirements ? 'italic' : 'normal',
                              }}>
                                {event.additional_requirements || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: alpha(theme.palette.warning.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 2 }}>
                        <InfoIcon color="primary" fontSize="large" />
                        Important Notes
                      </Typography>
                      <List sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                        <ListItem>
                          <ListItemIcon>
                            <InfoIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Cancellation Policy
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.cancellation_policy ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.cancellation_policy ? 'italic' : 'normal',
                              }}>
                                {event.cancellation_policy || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <InfoIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Refund Policy
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.refund_policy ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.refund_policy ? 'italic' : 'normal',
                              }}>
                                {event.refund_policy || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <InfoIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Additional Notes
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body1" sx={{ 
                                color: event.additional_notes ? 'text.primary' : 'text.disabled', 
                                fontSize: '1rem', 
                                lineHeight: 1.6,
                                fontStyle: !event.additional_notes ? 'italic' : 'normal',
                              }}>
                                {event.additional_notes || 'Not specified'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Organizer Information */}
          <Paper 
            sx={{ 
              p: 3, 
              mb: 4,
              bgcolor: alpha(theme.palette.secondary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: 'text.primary', mb: 2 }}>
              <StoreIcon color="primary" fontSize="large" />
              Organizer Information
            </Typography>
            <Divider sx={{ mb: 3, borderWidth: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {event.organizer?.profile_picture_url && (
                    <Avatar
                      src={getImageUrl(event.organizer.profile_picture_url)}
                      sx={{ width: 56, height: 56 }}
                    />
                  )}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 1 }}>
                      <StoreIcon color="primary" fontSize="medium" />
                      Event Organizer
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                      {event.organizer?.organization_name || 'Unknown Organizer'}
                    </Typography>
                    {event.organizer?.bio && (
                      <Typography variant="body1" sx={{ mt: 1, color: 'text.primary', fontSize: '1rem', lineHeight: 1.7 }}>
                        {event.organizer.bio}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              {event.organizer?.contact_info && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.primary', mb: 1 }}>
                      <EmailIcon color="primary" fontSize="medium" />
                      Contact Information
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.05rem', color: 'text.primary', lineHeight: 1.6 }}>
                      {event.organizer.contact_info}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Edit Button for Organizers */}
          {isOrganizer && (
            <Card sx={{ mb: 4, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                {profileLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Event Management
                    </Typography>
                    {isEventOwner ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                        size="large"
                        sx={{ mb: 2 }}
                      >
                        Edit Event Details
                      </Button>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        You can only edit events you created. Event organizer ID: {event?.organizer_id}, Your organizer ID: {organizerProfile?.organizer_id}
                      </Alert>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
          {/* Quick Info Card */}
          <Card 
            sx={{ 
              mb: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: 3, pb: 2, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                Quick Info
              </Typography>
              <Stack spacing={3}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'primary.main', mb: 1.5 }}>
                    <EventIcon fontSize="medium" />
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.15rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                    {format(new Date(event.date), 'MMMM d, yyyy')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1rem', fontWeight: 500 }}>
                    {format(new Date(event.date), 'h:mm a')}
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'info.main', mb: 1.5 }}>
                    <LocationIcon fontSize="medium" />
                    Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.6 }}>
                    {event.location}
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'success.main', mb: 1.5 }}>
                    <MoneyIcon fontSize="medium" />
                    Vendor Fee
                  </Typography>
                  {event.table_tiers && Array.isArray(event.table_tiers) && event.table_tiers.length > 0 ? (
                    <Box>
                      <Stack spacing={1.5}>
                        {event.table_tiers.map((tier, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: tier.fee === 0 
                                ? `linear-gradient(135deg, ${alpha('#4caf50', 0.12)} 0%, ${alpha('#4caf50', 0.04)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                              border: `2px solid ${tier.fee === 0 ? alpha('#4caf50', 0.3) : alpha(theme.palette.primary.main, 0.3)}`,
                              boxShadow: `0 2px 8px ${tier.fee === 0 ? alpha('#4caf50', 0.15) : alpha(theme.palette.primary.main, 0.15)}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${tier.fee === 0 ? alpha('#4caf50', 0.25) : alpha(theme.palette.primary.main, 0.25)}`,
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: tier.description ? 1 : 0 }}>
                              <Box>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: tier.fee === 0 ? '#2e7d32' : theme.palette.primary.dark,
                                    mb: 0.5
                                  }}
                                >
                                  {tier.name || `Tier ${idx + 1}`}
                                </Typography>
                                {tier.description && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: tier.fee === 0 ? alpha('#2e7d32', 0.7) : alpha(theme.palette.primary.dark, 0.7),
                                      lineHeight: 1.4,
                                      mb: 0.5
                                    }}
                                  >
                                    {tier.description}
                                  </Typography>
                                )}
                                {tier.capacity !== null && tier.capacity !== undefined && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      display: 'block',
                                      mt: 0.5,
                                      fontWeight: 500
                                    }}
                                  >
                                    Capacity: {tier.capacity} spots
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  ml: 2,
                                  px: 2,
                                  py: 1,
                                  borderRadius: 2,
                                  bgcolor: tier.fee === 0 
                                    ? alpha('#4caf50', 0.15) 
                                    : alpha('#4caf50', 0.15),
                                  border: `1px solid ${tier.fee === 0 ? '#4caf50' : '#4caf50'}`,
                                }}
                              >
                                <Typography 
                                  variant="h5" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    lineHeight: 1,
                                    color: '#2e7d32'
                                  }}
                                >
                                  {tier.fee === 0 ? 'Free' : `$${parseFloat(tier.fee).toFixed(2)}`}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '2rem', color: '#2e7d32' }}>
                      {event.fee ? `$${parseFloat(event.fee).toFixed(2)}` : 'Free'}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'warning.main', mb: 1.5 }}>
                    <PeopleIcon fontSize="medium" />
                    Spots
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 1 }}>
                    {eventStats?.confirmed_spots ?? 0} / {event.capacity || '∞'} confirmed
                  </Typography>
                  {eventStats && (
                    <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '1rem', mb: 1 }}>
                      {eventStats.total_applicants} applicants{eventStats.waitlist_count > 0 && ` • ${eventStats.waitlist_count} waitlisted`}
                    </Typography>
                  )}
                  {(() => {
                    const isFull = event && event.capacity && eventStats && 
                                 typeof eventStats.confirmed_spots === 'number' && 
                                 eventStats.confirmed_spots >= event.capacity;
                    return isFull ? (
                      <Chip 
                        label="Event Full - Join Waitlist" 
                        color="warning" 
                        size="small" 
                        sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                      />
                    ) : null;
                  })()}
                </Box>
                {isApplicationDeadlinePassed && (
                  <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="body2">
                      Application deadline has passed. The deadline was {event?.application_deadline ? format(new Date(event.application_deadline), 'MMMM d, yyyy \'at\' h:mm a') : ''}.
                    </Typography>
                  </Alert>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => setApplicationDialogOpen(true)}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    }
                  }}
                  disabled={Boolean(myAppForThisEvent) || isApplicationDeadlinePassed}
                  color={(() => {
                    const isFull = event && event.capacity && eventStats && 
                                 typeof eventStats.confirmed_spots === 'number' && 
                                 eventStats.confirmed_spots >= event.capacity;
                    return isFull ? 'warning' : 'primary';
                  })()}
                >
                  {(() => {
                    // Check if event is full
                    const isFull = event && event.capacity && eventStats && 
                                 typeof eventStats.confirmed_spots === 'number' && 
                                 eventStats.confirmed_spots >= event.capacity;
                    
                    // If user already applied
                    if (myAppForThisEvent) {
                      const appStatus = myAppForThisEvent.status?.toLowerCase();
                      // Check for waitlist status or waitlist position
                      if (appStatus === 'waitlist' || myAppForThisEvent.waitlist_position) {
                        const position = myAppForThisEvent.waitlist_position ? ` #${myAppForThisEvent.waitlist_position}` : '';
                        return `Waitlisted${position ? ` (Position${position})` : ''}`;
                      }
                      if (myAppForThisEvent.status) {
                        const status = myAppForThisEvent.status;
                        return `Application ${status.charAt(0).toUpperCase()}${status.slice(1)}`;
                      }
                      return 'Application Submitted';
                    }
                    
                    // If event is full and user hasn't applied, show Join Waitlist
                    if (isFull) {
                      return 'Join Waitlist';
                    }
                    
                    // Otherwise show Apply as Vendor
                    return 'Apply as Vendor';
                  })()}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Important Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Important Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon fontSize="small" />
                  Setup Time
                </Typography>
                <Typography variant="body2">
                  {event.setup_time || 'Not specified'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon fontSize="small" />
                  Requirements
                </Typography>
                <Typography variant="body2">
                  {event.requirements || 'No specific requirements'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon fontSize="small" />
                  Additional Notes
                </Typography>
                <Typography variant="body2">
                  {event.additional_notes || 'No additional notes'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Application Dialog */}
      <Dialog 
        open={applicationDialogOpen} 
        onClose={() => {
          setApplicationDialogOpen(false);
          setQuestionAnswers({});
          setSelectedCheckboxes([]);
          setApplicationNote('');
          setSelectedTierId(null);
          setApplicationFile(null);
          setError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apply as Vendor</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {/* Table/Booth Tier Selection */}
            {event?.table_tiers && event.table_tiers.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Table/Booth Type *</InputLabel>
                <Select
                  value={selectedTierId || ''}
                  onChange={(e) => setSelectedTierId(e.target.value ? parseInt(e.target.value) : null)}
                  label="Select Table/Booth Type *"
                >
                  {event.table_tiers.map((tier) => {
                    // Calculate confirmed count for this tier from eventStats
                    const tierConfirmedCount = eventStats?.tier_stats?.[tier.id]?.confirmed || 0;
                    const tierCapacity = tier.capacity;
                    const isTierFull = tierCapacity !== null && tierCapacity !== undefined && tierConfirmedCount >= tierCapacity;
                    
                    return (
                      <MenuItem 
                        key={tier.id} 
                        value={tier.id}
                        disabled={isTierFull}
                      >
                        <Box>
                          <Typography variant="body1">
                            {tier.name} - ${tier.fee?.toFixed(2) || '0.00'}
                            {tier.description && ` (${tier.description})`}
                          </Typography>
                          {tierCapacity !== null && tierCapacity !== undefined && (
                            <Typography variant="caption" color={isTierFull ? 'error' : 'text.secondary'}>
                              {isTierFull ? `Full (${tierConfirmedCount}/${tierCapacity})` : `${tierConfirmedCount}/${tierCapacity} spots taken`}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  Choose the table/booth type you'd like to apply for
                </FormHelperText>
              </FormControl>
            )}

            {/* Custom Checkboxes */}
            {event?.custom_checkboxes && event.custom_checkboxes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Select applicable options:
                </Typography>
                <FormGroup>
                  {event.custom_checkboxes.map((checkbox) => (
                    <FormControlLabel
                      key={checkbox.id}
                      control={
                        <Checkbox
                          checked={selectedCheckboxes.includes(checkbox.id)}
                          onChange={() => handleCheckboxChange(checkbox.id)}
                        />
                      }
                      label={checkbox.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}

            {/* Custom Questions */}
            {event?.custom_questions && event.custom_questions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Please answer the following questions:
                </Typography>
                {event.custom_questions.map((question) => (
                  <Box key={question.id} sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      {question.question}
                      {question.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>
                    {question.type === 'text' && (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={questionAnswers[question.id] || ''}
                        onChange={(e) => setQuestionAnswers({
                          ...questionAnswers,
                          [question.id]: e.target.value
                        })}
                        placeholder="Your answer..."
                        required={question.required}
                      />
                    )}
                    {question.type === 'checkbox' && (
                      <FormGroup>
                        {question.options && question.options.map((option, optIndex) => (
                          <FormControlLabel
                            key={optIndex}
                            control={
                              <Checkbox
                                checked={(questionAnswers[question.id] || []).includes(option)}
                                onChange={(e) => {
                                  const currentAnswers = questionAnswers[question.id] || [];
                                  if (e.target.checked) {
                                    setQuestionAnswers({
                                      ...questionAnswers,
                                      [question.id]: [...currentAnswers, option]
                                    });
                                  } else {
                                    setQuestionAnswers({
                                      ...questionAnswers,
                                      [question.id]: currentAnswers.filter(a => a !== option)
                                    });
                                  }
                                }}
                              />
                            }
                            label={option}
                          />
                        ))}
                      </FormGroup>
                    )}
                    {question.type === 'options' && (
                      <FormControl fullWidth>
                        <Select
                          value={questionAnswers[question.id] || ''}
                          onChange={(e) => setQuestionAnswers({
                            ...questionAnswers,
                            [question.id]: e.target.value
                          })}
                          required={question.required}
                        >
                          <MenuItem value="">
                            <em>Select an option</em>
                          </MenuItem>
                          {question.options && question.options.map((option, optIndex) => (
                            <MenuItem key={optIndex} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Why do you want to participate in this event?"
              value={applicationNote}
              onChange={(e) => setApplicationNote(e.target.value)}
              placeholder="Tell us about your business and why you'd be a great fit for this event..."
              sx={{ mb: 2 }}
            />
            
            {/* File Upload */}
            <Box sx={{ mb: 2 }}>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="application-file-upload"
                type="file"
                onChange={(e) => setApplicationFile(e.target.files[0] || null)}
              />
              <label htmlFor="application-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  {applicationFile ? applicationFile.name : 'Attach Document (Optional)'}
                </Button>
              </label>
              {applicationFile && (
                <Button
                  size="small"
                  onClick={() => setApplicationFile(null)}
                  sx={{ mt: 1 }}
                >
                  Remove
                </Button>
              )}
              <FormHelperText>
                Upload any required documents, certifications, or additional information
              </FormHelperText>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Your application will be reviewed by the event organizer. They may contact you for more information.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApply}
            disabled={submitApplicationMutation.isLoading}
            sx={{ 
              minWidth: 120,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }}
          >
            {submitApplicationMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Submit Application'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="event-image-upload"
                  type="file"
                  onChange={(e) => setEditImageFile(e.target.files[0] || null)}
                />
                <label htmlFor="event-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {editImageFile ? editImageFile.name : 'Upload Event Image (Optional)'}
                  </Button>
                </label>
                {editImageFile && (
                  <Button
                    size="small"
                    onClick={() => setEditImageFile(null)}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                )}
                {eventImageUrl && !editImageFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Current image will be kept if no new image is uploaded
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={editFormData.title || ''}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={editFormData.description || ''}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.is_permanent || false}
                    onChange={(e) => {
                      setEditFormData(prev => ({
                        ...prev,
                        is_permanent: e.target.checked,
                        date: e.target.checked ? '' : prev.date,  // Clear date if making permanent
                        application_deadline: e.target.checked ? '' : prev.application_deadline,  // Clear deadline if making permanent
                      }));
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Permanent/Recurring Event
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Check this for year-round events (e.g., permanent farmer's market). Date and application deadline are not required.
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label={editFormData.is_permanent ? "Event Date (Optional)" : "Event Date"}
                value={editFormData.date || ''}
                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                disabled={editFormData.is_permanent}
                InputLabelProps={{ shrink: true }}
                helperText={editFormData.is_permanent ? "Leave empty for permanent events" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Application Deadline (Optional)"
                value={editFormData.application_deadline || ''}
                onChange={(e) => setEditFormData({ ...editFormData, application_deadline: e.target.value })}
                disabled={editFormData.is_permanent}
                InputLabelProps={{ shrink: true }}
                helperText={editFormData.is_permanent ? "Not applicable for permanent events" : "Must be before the event date. Leave empty to allow applications until the event date."}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={editFormData.location || ''}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Capacity"
                value={editFormData.capacity || ''}
                onChange={(e) => setEditFormData({ ...editFormData, capacity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Vendor Fee (Optional)"
                value={editFormData.fee || ''}
                onChange={(e) => setEditFormData({ ...editFormData, fee: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>
                  ),
                }}
                helperText="Fee vendors need to pay to participate"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Setup Information</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Setup Time"
                value={editFormData.setup_time || ''}
                onChange={(e) => setEditFormData({ ...editFormData, setup_time: e.target.value })}
                placeholder="e.g., 6:00 AM - 8:00 AM"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Power Availability"
                value={editFormData.power_availability || ''}
                onChange={(e) => setEditFormData({ ...editFormData, power_availability: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Facilities"
                value={editFormData.facilities || ''}
                onChange={(e) => setEditFormData({ ...editFormData, facilities: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Security & Safety</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Security Measures"
                value={editFormData.security_measures || ''}
                onChange={(e) => setEditFormData({ ...editFormData, security_measures: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Insurance Requirements"
                value={editFormData.insurance_requirements || ''}
                onChange={(e) => setEditFormData({ ...editFormData, insurance_requirements: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Safety Guidelines"
                value={editFormData.safety_guidelines || ''}
                onChange={(e) => setEditFormData({ ...editFormData, safety_guidelines: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Payment Information</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Payment Instructions"
                value={editFormData.payment_instructions || ''}
                onChange={(e) => setEditFormData({ ...editFormData, payment_instructions: e.target.value })}
                helperText="Instructions on how vendors should submit payment"
                placeholder="e.g., Send payment via e-transfer to..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Payment Deadline (Days)"
                value={editFormData.payment_deadline_days || ''}
                onChange={(e) => setEditFormData({ ...editFormData, payment_deadline_days: e.target.value })}
                inputProps={{ min: 1, max: 30 }}
                helperText="Days after acceptance to pay"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Vendor Requirements</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Required Documents"
                value={editFormData.required_documents || ''}
                onChange={(e) => setEditFormData({ ...editFormData, required_documents: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Vendor Requirements"
                value={editFormData.vendor_requirements || ''}
                onChange={(e) => setEditFormData({ ...editFormData, vendor_requirements: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Equipment Needed"
                value={editFormData.equipment_needed || ''}
                onChange={(e) => setEditFormData({ ...editFormData, equipment_needed: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Requirements"
                value={editFormData.additional_requirements || ''}
                onChange={(e) => setEditFormData({ ...editFormData, additional_requirements: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Policies</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Cancellation Policy"
                value={editFormData.cancellation_policy || ''}
                onChange={(e) => setEditFormData({ ...editFormData, cancellation_policy: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Refund Policy"
                value={editFormData.refund_policy || ''}
                onChange={(e) => setEditFormData({ ...editFormData, refund_policy: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Notes"
                value={editFormData.additional_notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, additional_notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={updateEventMutation.isLoading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetail;